import {
  SystemNotificationType,
  type SystemNotification,
} from "~/types/system_notifications.types";
import { VerificationStatus } from "~/types/enums";

type NotificationsResponse = {
  notifications: SystemNotification[];
  unreadCount: number;
};

// An approved claim changes the user's role and owned profiles on the server, which the
// app only loads at startup, so it needs a full reload to show them.
function isApprovedClaim(notification: SystemNotification) {
  return (
    notification.type === SystemNotificationType.CLAIM_STATUS_UPDATED &&
    notification.metadata?.status === VerificationStatus.APPROVED
  );
}

export const useNotificationsStore = defineStore("notificationsStore", {
  state: () => ({
    notifications: [] as SystemNotification[],
    unreadCount: 0,
    loading: false,
    initialized: false,
    refreshInterval: null as ReturnType<typeof setInterval> | null,
    // Set when a claim approval arrives while the app is open; the layout asks the
    // user to reload.
    approvedClaimEntityId: null as string | null,
    _refreshHandler: null as (() => void) | null,
  }),

  actions: {
    async fetchNotifications(force = false) {
      if (this.loading || (this.initialized && !force)) {
        return;
      }

      this.loading = true;

      try {
        const response =
          await usePrivateApiFetch()<NotificationsResponse>(
            "/api/notifications",
          );

        const previousIds = new Set(this.notifications.map((item) => item.id));
        // On the first fetch the app state is fresh already.
        const approvedClaim = this.initialized
          ? response.notifications.find(
              (item) =>
                !previousIds.has(item.id) && !item.read_at && isApprovedClaim(item),
            )
          : undefined;

        this.notifications = response.notifications;
        this.unreadCount = response.unreadCount;
        this.initialized = true;

        if (approvedClaim) {
          this.approvedClaimEntityId =
            approvedClaim.target_entity_id ??
            (approvedClaim.metadata?.entity_id as string | undefined) ??
            null;
        }
      } finally {
        this.loading = false;
      }
    },

    async fetchUnreadCount() {
      const response = await usePrivateApiFetch()<{
        count: number;
      }>("/api/notifications/unread-count");

      if (this.initialized && response.count !== this.unreadCount) {
        await this.fetchNotifications(true);
        return;
      }

      this.unreadCount = response.count;
    },

    async markAsRead(id: string) {
      const notification = this.notifications.find((item) => item.id === id);
      const wasUnread = Boolean(notification && !notification.read_at);

      if (notification && !notification.read_at) {
        notification.read_at = new Date().toISOString();
      }

      if (wasUnread) {
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      }

      try {
        await usePrivateApiFetch()<SystemNotification>(
          `/api/notifications/${id}/read`,
          { method: "POST" },
        );
      } catch (error) {
        if (notification && wasUnread) {
          notification.read_at = null;
          this.unreadCount += 1;
        }
        throw error;
      }
    },

    async markAllAsRead() {
      const previousNotifications = this.notifications.map((notification) => ({
        id: notification.id,
        read_at: notification.read_at,
      }));
      const previousUnreadCount = this.unreadCount;
      const now = new Date().toISOString();

      this.notifications.forEach((notification) => {
        notification.read_at = notification.read_at ?? now;
      });
      this.unreadCount = 0;

      try {
        await usePrivateApiFetch()("/api/notifications/read-all", {
          method: "POST",
        });
      } catch (error) {
        previousNotifications.forEach((previous) => {
          const notification = this.notifications.find(
            (item) => item.id === previous.id,
          );
          if (notification) {
            notification.read_at = previous.read_at;
          }
        });
        this.unreadCount = previousUnreadCount;
        throw error;
      }
    },

    async openNotification(notification: SystemNotification) {
      if (!notification.read_at) {
        await this.markAsRead(notification.id);
      }

      if (isApprovedClaim(notification)) {
        await reloadNuxtApp({ path: notification.action_path ?? "/" });
        return;
      }

      if (notification.action_path) {
        const route = useRoute();
        const resolved = useRouter().resolve(notification.action_path);
        const isSamePage =
          route.path === resolved.path &&
          JSON.stringify(route.query) === JSON.stringify(resolved.query);

        if (isSamePage) {
          await reloadNuxtApp({ path: notification.action_path });
        } else {
          await navigateTo(notification.action_path);
        }
      }
    },

    startAutoRefresh() {
      if (!process.client || this.refreshInterval) return;
      if (!useUserStore().isUserLoggedIn) return;

      const refreshIfVisible = () => {
        if (document.visibilityState === "visible") {
          this.fetchUnreadCount().catch((error) => {
            usePosthog().logError(
              error,
              "notifications.ts:startAutoRefresh()",
              "fetching notification unread count failed",
            );
          });
        }
      };

      this._refreshHandler = refreshIfVisible;
      window.addEventListener("focus", refreshIfVisible);
      document.addEventListener("visibilitychange", refreshIfVisible);

      this.refreshInterval = setInterval(() => {
        refreshIfVisible();
      }, 30_000);
    },

    stopAutoRefresh() {
      if (!process.client) return;

      if (this._refreshHandler) {
        window.removeEventListener("focus", this._refreshHandler);
        document.removeEventListener("visibilitychange", this._refreshHandler);
        this._refreshHandler = null;
      }

      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },

    reset() {
      this.stopAutoRefresh();
      this.notifications = [];
      this.unreadCount = 0;
      this.loading = false;
      this.initialized = false;
      this.approvedClaimEntityId = null;
    },
  },
});
